import React, { useRef, useState } from 'react';

import { observer } from 'mobx-react';
import classnames from 'classnames';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { Button, Checkbox } from '@bringg/react-components';
import { DetailedCellProps, useRowHover, ViewItemButton } from '@bringg-frontend/monitoring';
import { GroupData } from '@bringg/types';

import notification from 'bringg-web/services/notification';
import useOrderExplorerStore from '../../stores/use-order-explorer-store';
import useOrderExplorerTranslations from '../../use-order-explorer-translations';
import { isMasterGroup, isSubGroup, isTwoLevelsGroup } from '../../../shared/utils/grouping';
import { useGroupSelection } from '../../../shared/hooks/use-group-selection';
import { useViewableGroups } from '../../../shared/hooks/use-viewable-groups';
import { groupToggleService } from '../../data/group-toggle-service';
import ColorIndicator from '../../../shared/components/color-indicator/color-indicator';
import RouteGroupTags from '../route-grop-tags/route-group-tags';
import RouteRow from '../../../shared/components/row-identifier/route-row';

import styles from './group-row.module.scss';

const GroupRow = ({ row }: DetailedCellProps<GroupData>) => {
	const ref = useRef<HTMLDivElement>(null);
	const { orderExplorerStore } = useOrderExplorerStore();
	const { unassigned, noTeam, noRoute } = useOrderExplorerTranslations();
	const { isSelected, isPartiallySelected, selectGroup } = useGroupSelection(row);
	const { isInView, isItemViewable, viewGroup } = useViewableGroups(row);
	const { failedToFetch } = useOrderExplorerTranslations();
	const [loading, setLoading] = useState<boolean>(false);

	const {
		data: { title },
		group_type,
		id
	} = row;

	useRowHover(ref, id);

	const isCollapsed = orderExplorerStore.currentTableDefinition.isCollapsed(id);

	const isFirstLevelGroup =
		(isTwoLevelsGroup(orderExplorerStore.currentTableDefinition.groupMode) && isMasterGroup(group_type)) ||
		isSubGroup(group_type);
	const isSecondLevelGroup =
		isTwoLevelsGroup(orderExplorerStore.currentTableDefinition.groupMode) && isSubGroup(group_type);

	const getGroupTitle = (): string | React.JSX.Element => {
		if (title) {
			return group_type === 'route' ? <RouteRow routeId={row.data.id} /> : title;
		}
		switch (group_type) {
			case 'route':
				return noRoute;
			case 'team':
				return noTeam;
			default:
				return unassigned;
		}
	};

	const onGroupSelect = async ({ target }) => {
		try {
			await selectGroup(target.checked);
		} catch (err) {
			notification.error(`${failedToFetch} ${err}`);
			console.error('failed to load ', err);
		}
	};

	const onGroupView = async (_, _isInView: boolean) => {
		setLoading(true);
		try {
			await viewGroup(_isInView);
		} catch (err) {
			notification.error(failedToFetch);
			console.error('failed to fetch group items to view on the map', err);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div
			className={classnames(styles.row, styles.group, `row-${id}`, {
				[styles.firstLevelGroup]: isFirstLevelGroup,
				[styles.secondLevelGroup]: isSecondLevelGroup
			})}
			ref={ref}
			data-test-id={`group-row-${id}`}
		>
			<div className={styles.leftSide}>
				{group_type !== 'team' && (
					<ColorIndicator color={orderExplorerStore.getItemColor(id)} dataTestId={`color-group-${id}`} />
				)}
				<Button
					className={styles.collapseBtn}
					onClick={async () => groupToggleService.toggleCollapse(row, !isCollapsed)}
					type="ghost"
					data-test-id={`collapse-btn-${id}`}
				>
					<BringgIcon iconName={isCollapsed ? BringgFontIcons.ChevronRight : BringgFontIcons.Chevron} />
				</Button>
				<Checkbox
					className={styles.checkbox}
					indeterminate={isPartiallySelected}
					checked={isSelected}
					onChange={onGroupSelect}
					data-test-id={`select-${id}`}
				/>
				{isItemViewable && (
					<ViewItemButton loading={loading} onClick={onGroupView} isInView={isInView} item={row} />
				)}
				<div className={styles.title}>{getGroupTitle()}</div>
			</div>
			<div className={styles.rightSide}>
				{row.data.id && row.group_type === 'route' && <RouteGroupTags routeId={row.data.id} groupId={id} />}
			</div>
		</div>
	);
};

export default observer(GroupRow);
